import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { WalletBalance, WalletTransaction } from '../../services/api/types';
import { walletService } from '../../services/api';

interface WalletState {
  // Wallet data
  balance: WalletBalance | null;
  recentTransactions: WalletTransaction[];
  
  // Loading states
  loading: {
    balance: boolean;
    transactions: boolean;
    topup: boolean;
    payment: boolean;
  };
  
  // Khalti payment state
  currentPayment: {
    amount: number | null;
    pidx: string | null;
    payment_url: string | null;
    transaction_id: string | null;
    status: 'idle' | 'initiated' | 'processing' | 'completed' | 'failed';
  };
  
  // UI state
  showTopupModal: boolean;
  selectedTopupAmount: number | null;
  customTopupAmount: string;
  
  // Error handling
  error: string | null;
  
  // Cache timing
  lastBalanceRefresh: number;
}

const initialState: WalletState = {
  balance: null,
  recentTransactions: [],
  loading: {
    balance: false,
    transactions: false,
    topup: false,
    payment: false,
  },
  currentPayment: {
    amount: null,
    pidx: null,
    payment_url: null,
    transaction_id: null,
    status: 'idle',
  },
  showTopupModal: false,
  selectedTopupAmount: null,
  customTopupAmount: '',
  error: null,
  lastBalanceRefresh: 0,
};

// Async thunks
export const fetchWalletBalance = createAsyncThunk(
  'wallet/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const balance = await walletService.getWalletBalance();
      return balance;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRecentTransactions = createAsyncThunk(
  'wallet/fetchRecentTransactions',
  async (limit: number = 5, { rejectWithValue }) => {
    try {
      const transactions = await walletService.getRecentTransactions(limit);
      return transactions;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const initiateKhaltiTopup = createAsyncThunk(
  'wallet/initiateKhaltiTopup',
  async (amount: number, { rejectWithValue }) => {
    try {
      // Validate amount
      const validation = walletService.validateTopupAmount(amount);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
      
      const response = await walletService.initiateKhaltiTopup(amount);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyKhaltiPayment = createAsyncThunk(
  'wallet/verifyKhaltiPayment',
  async (
    { pidx, transactionId, status }: { pidx: string; transactionId?: string; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const result = await walletService.verifyKhaltiPayment({
        pidx,
        transaction_id: transactionId,
        status,
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkSufficientBalance = createAsyncThunk(
  'wallet/checkSufficientBalance',
  async (requiredAmount: number, { rejectWithValue }) => {
    try {
      const result = await walletService.checkSufficientBalance(requiredAmount);
      return { requiredAmount, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getOptimalTopupAmount = createAsyncThunk(
  'wallet/getOptimalTopupAmount',
  async (packPrice: number, { rejectWithValue }) => {
    try {
      const result = await walletService.getOptimalTopupAmount(packPrice);
      return { packPrice, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    // UI actions
    showTopupModal: (state) => {
      state.showTopupModal = true;
      state.error = null;
    },
    
    hideTopupModal: (state) => {
      state.showTopupModal = false;
      state.selectedTopupAmount = null;
      state.customTopupAmount = '';
      state.error = null;
    },
    
    setSelectedTopupAmount: (state, action: PayloadAction<number>) => {
      state.selectedTopupAmount = action.payload;
      state.customTopupAmount = '';
    },
    
    setCustomTopupAmount: (state, action: PayloadAction<string>) => {
      state.customTopupAmount = action.payload;
      state.selectedTopupAmount = null;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Payment flow actions
    resetPaymentState: (state) => {
      state.currentPayment = {
        amount: null,
        pidx: null,
        payment_url: null,
        transaction_id: null,
        status: 'idle',
      };
      state.error = null;
    },
    
    setPaymentStatus: (state, action: PayloadAction<WalletState['currentPayment']['status']>) => {
      state.currentPayment.status = action.payload;
    },
    
    // Update balance after transactions
    updateBalanceAfterTransaction: (state, action: PayloadAction<{ 
      newBalance: number; 
      transaction: WalletTransaction 
    }>) => {
      if (state.balance) {
        state.balance.current_balance = action.payload.newBalance;
        state.balance.last_updated = new Date().toISOString();
      }
      
      // Add transaction to recent list
      state.recentTransactions.unshift(action.payload.transaction);
      
      // Keep only recent 10 transactions
      state.recentTransactions = state.recentTransactions.slice(0, 10);
      
      state.lastBalanceRefresh = Date.now();
    },
    
    // Force refresh trigger
    triggerBalanceRefresh: (state) => {
      state.lastBalanceRefresh = 0; // Force refresh on next access
    },
  },
  
  extraReducers: (builder) => {
    // Fetch wallet balance
    builder
      .addCase(fetchWalletBalance.pending, (state) => {
        state.loading.balance = true;
        state.error = null;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.loading.balance = false;
        state.balance = action.payload;
        state.lastBalanceRefresh = Date.now();
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.loading.balance = false;
        state.error = action.payload as string;
      });
    
    // Fetch recent transactions
    builder
      .addCase(fetchRecentTransactions.pending, (state) => {
        state.loading.transactions = true;
      })
      .addCase(fetchRecentTransactions.fulfilled, (state, action) => {
        state.loading.transactions = false;
        state.recentTransactions = action.payload;
      })
      .addCase(fetchRecentTransactions.rejected, (state, action) => {
        state.loading.transactions = false;
        state.error = action.payload as string;
      });
    
    // Initiate Khalti top-up
    builder
      .addCase(initiateKhaltiTopup.pending, (state) => {
        state.loading.topup = true;
        state.error = null;
        state.currentPayment.status = 'initiated';
      })
      .addCase(initiateKhaltiTopup.fulfilled, (state, action) => {
        state.loading.topup = false;
        state.currentPayment = {
          amount: action.payload.amount,
          pidx: action.payload.pidx,
          payment_url: action.payload.payment_url,
          transaction_id: null,
          status: 'processing',
        };
      })
      .addCase(initiateKhaltiTopup.rejected, (state, action) => {
        state.loading.topup = false;
        state.error = action.payload as string;
        state.currentPayment.status = 'failed';
      });
    
    // Verify Khalti payment
    builder
      .addCase(verifyKhaltiPayment.pending, (state) => {
        state.loading.payment = true;
        state.currentPayment.status = 'processing';
      })
      .addCase(verifyKhaltiPayment.fulfilled, (state, action) => {
        state.loading.payment = false;
        state.currentPayment.status = action.payload.success ? 'completed' : 'failed';

        if (!action.payload.success) {
          state.error = action.payload.message;
          return;
        }

        // Update balance
        if (state.balance && action.payload.new_balance !== undefined && action.payload.new_balance !== null) {
          state.balance.current_balance = action.payload.new_balance;
          state.balance.last_updated = new Date().toISOString();
        }
        
        // Add transaction to recent list
        if (action.payload.transaction) {
          state.recentTransactions.unshift(action.payload.transaction);
          state.recentTransactions = state.recentTransactions.slice(0, 10);
        }
        
        state.lastBalanceRefresh = Date.now();
        
        // Close modal on success
        state.showTopupModal = false;
        state.selectedTopupAmount = null;
        state.customTopupAmount = '';
      })
      .addCase(verifyKhaltiPayment.rejected, (state, action) => {
        state.loading.payment = false;
        state.error = action.payload as string;
        state.currentPayment.status = 'failed';
      });
    
    // Check sufficient balance
    builder
      .addCase(checkSufficientBalance.fulfilled, (state, action) => {
        // Store the check result in state for UI to use
        state.error = action.payload.sufficient 
          ? null 
          : `Insufficient balance. Need ${walletService.formatCurrency(action.payload.amount_needed)} more.`;
      });
    
    // Get optimal top-up amount
    builder
      .addCase(getOptimalTopupAmount.fulfilled, (state, action) => {
        // Auto-select recommended amount if no amount is currently selected
        if (!state.selectedTopupAmount && action.payload.recommended_topup > 0) {
          state.selectedTopupAmount = action.payload.recommended_topup;
        }
      });
  },
});

export const {
  showTopupModal,
  hideTopupModal,
  setSelectedTopupAmount,
  setCustomTopupAmount,
  clearError,
  resetPaymentState,
  setPaymentStatus,
  updateBalanceAfterTransaction,
  triggerBalanceRefresh,
} = walletSlice.actions;

export default walletSlice.reducer;

// Selectors
export const selectWalletBalance = (state: { wallet: WalletState }) => state.wallet.balance;
export const selectWalletLoading = (state: { wallet: WalletState }) => state.wallet.loading;
export const selectRecentTransactions = (state: { wallet: WalletState }) => state.wallet.recentTransactions;
export const selectCurrentPayment = (state: { wallet: WalletState }) => state.wallet.currentPayment;
export const selectShowTopupModal = (state: { wallet: WalletState }) => state.wallet.showTopupModal;
export const selectSelectedTopupAmount = (state: { wallet: WalletState }) => state.wallet.selectedTopupAmount;
export const selectCustomTopupAmount = (state: { wallet: WalletState }) => state.wallet.customTopupAmount;
export const selectWalletError = (state: { wallet: WalletState }) => state.wallet.error;
export const selectLastBalanceRefresh = (state: { wallet: WalletState }) => state.wallet.lastBalanceRefresh;

// Helper selectors
export const selectCurrentBalance = (state: { wallet: WalletState }) => 
  state.wallet.balance?.current_balance || 0;

export const selectCanAfford = (requiredAmount: number) => (state: { wallet: WalletState }) =>
  (state.wallet.balance?.current_balance || 0) >= requiredAmount;

export const selectTopupAmountNeeded = (requiredAmount: number) => (state: { wallet: WalletState }) =>
  Math.max(0, requiredAmount - (state.wallet.balance?.current_balance || 0));
